import React, { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { Typography } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';

import classnames from 'classnames';

import {
  ButtonEditModuleProperties,
  CollapseView,
  EmptyDataProduct,
} from 'pages/product-full-view/shared/components';

import { DisplayProductOverlayInfo } from './DisplayProductInfoOverlay';
import { WithLoading } from 'common/components';

import {
  productDetailActionTypes,
  useProductDetail,
  useProductDetailDispatch,
} from '../ProductDetailContext';

import {
  useCheckAllowEditProducts,
  useGetProductFullView,
  useGetProductOverlay,
} from 'hooks';

import { useGetProductItemModules } from '../queries';

import {
  getDataProductModule,
  getProductSchemaHasValidData,
  groupProductModuleProperties,
} from '../utils';

export const ProductAdvancedModulesOverlay = (props) => {
  const {
    productSchema,
    dataProductModules: productOverlayModules,
    productEnums,
    onClickEditBtn,
  } = props;

  const { id: productId } = useParams();

  const { productFull } = useGetProductFullView({
    productId,
  });

  const dispatch = useProductDetailDispatch();

  const {
    advancedModule: { activeModules, searchText, isShowOnlyValue },
    publicationError: { moduleNameError },
  } = useProductDetail();

  const { originalProductId } = useGetProductOverlay();

  const { dataProductModules: originalProductOverlay, isLoading } =
    useGetProductItemModules(originalProductId);

  const { checkAllowEditProductFull } = useCheckAllowEditProducts(productId);
  const isAllowEditProduct = checkAllowEditProductFull(productFull);

  const filteredProductSchema = useMemo(() => {
    if (!isShowOnlyValue) {
      return productSchema;
    } else {
      return getProductSchemaHasValidData(productOverlayModules, productSchema);
    }
  }, [isShowOnlyValue, productSchema, productOverlayModules]);

  const isEmptyProductSchema = filteredProductSchema?.length === 0;

  if (isEmptyProductSchema) {
    return (
      <WithLoading loading={isLoading}>
        <EmptyDataProduct />
      </WithLoading>
    );
  }
  return (
    <div className='product-detail-view__list-info scroller'>
      {filteredProductSchema.map((module) => {
        const hasModuleName = activeModules.includes(module.moduleName);
        const isActiveBtnEdit = hasModuleName && isAllowEditProduct;

        const foundModuleName = searchText?.split('.')[0]; // searchText is fieldFullPath of a property
        const isFoundModule =
          foundModuleName?.toLowerCase() === module?.moduleName?.toLowerCase();
        const isErrorPublicationModuleName =
          module.moduleName.toLowerCase() === moduleNameError.toLowerCase();

        return (
          <CollapseView
            activeKey={activeModules}
            key={module.moduleName}
            expandIconPosition='left'
            collapsible='header'
            keyPanel={module.moduleName}
            headerPanel={
              <Typography.Title
                level={5}
                className={classnames({
                  'product-detail-view__list-info-title': true,
                  [`product-detail-view__list-info-title--${module.moduleName}`]: true,
                  'product-detail-view__list-info-title--found-module':
                    isFoundModule,
                  'product-detail-view__list-info-title--publication-error':
                    isErrorPublicationModuleName,
                })}
              >
                {module.moduleDisplayName}
                {isErrorPublicationModuleName && (
                  <CloseCircleOutlined style={{ margin: '0 4px' }} />
                )}
              </Typography.Title>
            }
            extraPanel={() => {
              return (
                <>
                  {isActiveBtnEdit && (
                    <ButtonEditModuleProperties
                      moduleName={module.moduleName}
                      moduleDisplayName={module.moduleDisplayName}
                      onClick={onClickEditBtn}
                    />
                  )}
                </>
              );
            }}
            onChange={(value) => {
              dispatch({
                type: productDetailActionTypes.ACTIVE_MODULES,
                payload: {
                  value,
                  moduleView: 'advanced',
                },
              });
            }}
          >
            <DisplayProductOverlayInfo
              moduleProduct={groupProductModuleProperties(module)}
              productEnums={productEnums}
              dataProductOverlayModule={getDataProductModule(
                productOverlayModules,
                module.moduleName
              )}
              dataOriginalProductModule={getDataProductModule(
                originalProductOverlay,
                module.moduleName
              )}
            />
          </CollapseView>
        );
      })}
    </div>
  );
};
